/* app/CommandPalette.jsx — глобальная командная палитра (S13, §2.1).
   Модальный оверлей поиска по всему пространству: сущности (Сырьё/ТТК/Меню), чаты,
   действия, разделы. Вызов: Ctrl/⌘K (см. CommandPaletteContext) и кнопка в сайдбаре
   (на mobile «Найти» убрана — диалоги открываются иконкой чатов в шапке Ассистента).
   Поиск регистронезависим (кириллица). Клавиатура:
   ↑/↓ циклически, Enter — выполнить, Esc — закрыть. Клик = Enter.
   Адаптив: xl/lg/md — центрированный модал; sm — bottom-sheet; xs — полноэкранный.
   z-index: var(--z-command) (выше Modal/Drawer, ниже ConfirmDialog).
   ХЕНДОФФ → frontend/src/components/common/CommandPalette.jsx. */

import React from 'react';
import ReactDOM from 'react-dom';
import { useCommandPalette } from './CommandPaletteContext.jsx';
import { Icon } from '../components/ui/Icon.jsx';
import { getOverlayRoot, overlay } from '../lib/overlay.jsx';
import { RR } from '../lib/router.jsx';
import { searchIndex as __ns_searchIndex } from '../lib/searchIndex.js';

  const { useState, useEffect, useRef, useMemo, useCallback } = React;
  const { useNavigate } = RR;
  const { useScrollLock, useFocusTrap } = overlay;
  const { searchIndex } = __ns_searchIndex;

  const GROUP_ORDER = ['Сущности', 'Чаты', 'Действия', 'Разделы'];
  const PER_GROUP = 5;

  // строим отображаемые группы + плоский список выбираемых элементов (для клавиатуры)
  function buildView(ctx, query) {
    const groups = [];
    const flat = [];
    const push = (title, items) => {
      if (!items.length) return;
      const capped = items.slice(0, PER_GROUP);
      const more = items.length - capped.length;
      const start = flat.length;
      capped.forEach((e) => flat.push(e));
      groups.push({ title, items: capped, start, more });
    };

    const q = (query || '').trim();
    if (!q) {
      // пустой запрос → последние чаты + действия
      const recent = (ctx.recentChats || []).map((c) => ({
        id: 'chat-' + c.id, type: 'chat', label: c.title,
        sublabel: c.metadata && c.metadata.spawn_for_title ? ('↳ ' + c.metadata.spawn_for_title) : 'Чат',
        icon: 'chat', href: '/chat/' + c.id,
      }));
      push('Недавние чаты', recent);
      push('Действия', __ns_searchIndex.PALETTE_ACTIONS);
      return { groups, flat, empty: true };
    }

    const matched = searchIndex(ctx.index, q) || [];
    const byGroup = {};
    matched.forEach((e) => { (byGroup[e.group] = byGroup[e.group] || []).push(e); });
    GROUP_ORDER.forEach((g) => push(g, byGroup[g] || []));
    return { groups, flat, empty: false };
  }

  function Row({ entry, active, onHover, onPick, refCb }) {
    return (
      <button type="button"
        ref={refCb}
        className={'cmdp__row' + (active ? ' is-active' : '')}
        role="option" aria-selected={active}
        onMouseMove={onHover} onClick={onPick}>
        <span className="cmdp__row-glyph"><Icon name={entry.icon || 'search'} size={16} /></span>
        <span className="cmdp__row-text">
          <span className="cmdp__row-label truncate">{entry.label}</span>
          {entry.sublabel && <span className="cmdp__row-sub truncate">{entry.sublabel}</span>}
        </span>
        {entry.type === 'action' && <Icon name="plus" size={13} className="cmdp__row-tail" />}
        {entry.type !== 'action' && <Icon name="arrow-up-right" size={13} className="cmdp__row-tail" />}
      </button>
    );
  }

  function CommandPalette() {
    const ctx = useCommandPalette();
    const { open, closePalette } = ctx;
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [sel, setSel] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const rowRefs = useRef([]);
    const panelRef = useRef(null);

    useScrollLock(open);
    // focus-trap (overlay.jsx): циклический Tab внутри панели (input ↔ крестик ↔ строки),
    // Esc закрывает, фокус при открытии — на input, при закрытии возвращается на триггер.
    useFocusTrap(panelRef, { active: open, onEscape: closePalette, autoFocusSelector: '.cmdp__input' });

    const view = useMemo(() => buildView(ctx, query), [ctx, query]);
    const flat = view.flat;

    // сброс запроса/выделения при открытии
    useEffect(() => {
      if (open) { setQuery(''); setSel(0); }
    }, [open]);

    useEffect(() => { setSel(0); }, [query]);

    // держим выделенную строку в зоне видимости (без scrollIntoView)
    useEffect(() => {
      const el = rowRefs.current[sel];
      const list = listRef.current;
      if (!el || !list) return;
      const top = el.offsetTop, bottom = top + el.offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top - 8;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 8;
    }, [sel]);

    const pick = useCallback((entry) => {
      if (!entry) return;
      closePalette();
      if (entry.href) navigate(entry.href);
    }, [closePalette, navigate]);

    // Arrow/Enter живут на контейнере (работают и когда фокус на крестике/строке).
    // Tab и Esc обрабатывает useFocusTrap (циклический trap + закрытие с возвратом фокуса).
    const onKeyDown = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (flat.length ? (s + 1) % flat.length : 0)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(flat[sel]); }
    };

    if (!open) return null;

    rowRefs.current = [];
    let flatI = -1;

    return ReactDOM.createPortal(
      <div className="cmdp-overlay" style={{ zIndex: 'var(--z-command)' }} onMouseDown={(e) => { if (e.target === e.currentTarget) closePalette(); }}>
        <div className="cmdp" role="dialog" aria-modal="true" aria-label="Командная палитра" ref={panelRef} onKeyDown={onKeyDown}>
          <div className="cmdp__search">
            <Icon name="search" size={18} className="cmdp__search-icon" />
            <input
              ref={inputRef}
              className="cmdp__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по сущностям, чатам, действиям…"
              aria-label="Поиск"
              aria-controls="cmdp-list"
              autoComplete="off" spellCheck="false" />
            <button type="button" className="cmdp__close" onClick={closePalette} aria-label="Закрыть">
              <Icon name="close" size={18} />
            </button>
          </div>

          <div className="cmdp__list" id="cmdp-list" role="listbox" ref={listRef}>
            {flat.length === 0 ? (
              <div className="cmdp__empty">
                <Icon name="search" size={28} />
                <p className="t-body">Ничего не найдено</p>
                <p className="t-caption text-dim">Проверьте запрос или откройте раздел из списка.</p>
              </div>
            ) : (
              view.groups.map((g) => (
                <div className="cmdp__group" role="group" aria-label={g.title} key={g.title}>
                  <div className="cmdp__group-title">{g.title}</div>
                  {g.items.map((entry) => {
                    flatI += 1;
                    const myI = flatI;
                    return (
                      <Row key={entry.id} entry={entry} active={myI === sel}
                        refCb={(el) => { rowRefs.current[myI] = el; }}
                        onHover={() => setSel(myI)}
                        onPick={() => pick(entry)} />
                    );
                  })}
                  {g.more > 0 && <div className="cmdp__more t-caption text-mute">Ещё {g.more}… — уточните запрос</div>}
                </div>
              ))
            )}
          </div>

          <div className="cmdp__foot">
            <span className="cmdp__hint"><kbd className="cmdp__kbd">↑</kbd><kbd className="cmdp__kbd">↓</kbd> навигация</span>
            <span className="cmdp__hint"><kbd className="cmdp__kbd">↵</kbd> открыть</span>
            <span className="cmdp__hint"><kbd className="cmdp__kbd">esc</kbd> закрыть</span>
          </div>
        </div>
      </div>,
      getOverlayRoot()
    );
  }

export { CommandPalette };
